import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Save, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

interface CertificateTemplate {
  id?: string;
  name: string;
  description: string;
  canvas_data: any;
  background_image?: string;
  is_default: boolean;
  module_id?: string;
}

interface CertificateEditorProps {
  template?: CertificateTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Partial<CertificateTemplate>) => void;
}

export const CertificateEditor: React.FC<CertificateEditorProps> = ({
  template,
  isOpen,
  onClose,
  onSave
}) => {
  const { language } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    background_image: template?.canvas_data?.background || '',
    module_id: template?.module_id || '',
    company_name: template?.canvas_data?.elements?.footer || 'OVIT HR Department',
    certificate_title: template?.canvas_data?.elements?.title || 'CERTIFICATE',
    certificate_subtitle: template?.canvas_data?.elements?.subtitle || 'OF COMPLETION',
    logo_url: template?.canvas_data?.elements?.logo?.url || ''
  });
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(
    template?.canvas_data?.background || null
  );
  const [logoPreview, setLogoPreview] = useState<string | null>(
    template?.canvas_data?.elements?.logo?.url || null
  );
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [modules, setModules] = useState<Array<{id: string, title_en: string, title_el: string}>>([]);

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    // Update previews when template changes
    if (template) {
      setBackgroundPreview(template.canvas_data?.background || null);
      setLogoPreview(template.canvas_data?.elements?.logo?.url || null);
      setFormData({
        name: template.name || '',
        description: template.description || '',
        background_image: template.canvas_data?.background || '',
        module_id: template.module_id || '',
        company_name: template.canvas_data?.elements?.footer || 'OVIT HR Department',
        certificate_title: template.canvas_data?.elements?.title || 'CERTIFICATE',
        certificate_subtitle: template.canvas_data?.elements?.subtitle || 'OF COMPLETION',
        logo_url: template.canvas_data?.elements?.logo?.url || ''
      });
    }
  }, [template]);

  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('training_modules')
        .select('id, title_en, title_el')
        .eq('is_active', true)
        .order('title_en');
      
      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('certificate-backgrounds')
        .upload(fileName, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('certificate-backgrounds')
        .getPublicUrl(fileName);

      const imageUrl = urlData.publicUrl;
      setFormData(prev => ({ ...prev, background_image: imageUrl }));
      setBackgroundPreview(imageUrl);
      toast.success('Background image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload background image');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('certificate-backgrounds')
        .upload(fileName, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('certificate-backgrounds')
        .getPublicUrl(fileName);

      const imageUrl = urlData.publicUrl;
      setFormData(prev => ({ ...prev, logo_url: imageUrl }));
      setLogoPreview(imageUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeBackgroundImage = () => {
    setFormData(prev => ({ ...prev, background_image: '' }));
    setBackgroundPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    setLogoPreview(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    const templateData: Partial<CertificateTemplate> = {
      name: formData.name,
      description: formData.description,
      module_id: formData.module_id || undefined,
      canvas_data: {
        background: formData.background_image || '#ffffff',
        elements: {
          logo: { 
            type: 'logo', 
            position: 'top-center',
            url: formData.logo_url || ''
          },
          title: formData.certificate_title || 'CERTIFICATE',
          subtitle: formData.certificate_subtitle || 'OF COMPLETION',
          employeeName: 'Employee Name',
          trainingTitle: 'Training Title',
          badges: ['gold', 'silver', 'bronze'],
          footer: formData.company_name || 'OVIT HR Department',
          iso: 'Certified with ISO 9001:2015 Quality Management System'
        }
      },
      is_default: false
    };

    onSave(templateData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {template?.id 
              ? (language === 'en' ? 'Edit Certificate Template' : 'Επεξεργασία Προτύπου Πιστοποιητικού')
              : (language === 'en' ? 'Create Certificate Template' : 'Δημιουργία Προτύπου Πιστοποιητικού')
            }
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">
                {language === 'en' ? 'Template Name' : 'Όνομα Προτύπου'}
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={language === 'en' ? 'Enter template name' : 'Εισάγετε όνομα προτύπου'}
              />
            </div>

            <div>
              <Label htmlFor="description">
                {language === 'en' ? 'Description' : 'Περιγραφή'}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={language === 'en' ? 'Enter template description' : 'Εισάγετε περιγραφή προτύπου'}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="module">
                {language === 'en' ? 'Training Module' : 'Εκπαιδευτική Ενότητα'}
              </Label>
              <Select
                value={formData.module_id || 'none'}
                onValueChange={(value) => handleInputChange('module_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'en' ? 'Select a module (optional)' : 'Επιλέξτε ενότητα (προαιρετικό)'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {language === 'en' ? 'None' : 'Κανένα'}
                  </SelectItem>
                  {modules.map(module => (
                    <SelectItem key={module.id} value={module.id}>
                      {language === 'en' ? module.title_en : module.title_el}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="certificate_title">
                {language === 'en' ? 'Certificate Title' : 'Τίτλος Πιστοποιητικού'}
              </Label>
              <Input
                id="certificate_title"
                value={formData.certificate_title}
                onChange={(e) => handleInputChange('certificate_title', e.target.value)}
                placeholder="CERTIFICATE"
              />
            </div>

            <div>
              <Label htmlFor="certificate_subtitle">
                {language === 'en' ? 'Certificate Subtitle' : 'Υπότιτλος'}
              </Label>
              <Input
                id="certificate_subtitle"
                value={formData.certificate_subtitle}
                onChange={(e) => handleInputChange('certificate_subtitle', e.target.value)}
                placeholder="OF COMPLETION"
              />
            </div>

            <div>
              <Label htmlFor="company_name">
                {language === 'en' ? 'Company/Provider Name' : 'Όνομα Εταιρείας'}
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="OVIT HR Department"
              />
            </div>

            <div>
              <Label>
                {language === 'en' ? 'Company Logo' : 'Λογότυπο Εταιρείας'}
              </Label>
              <div className="space-y-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingLogo 
                    ? (language === 'en' ? 'Uploading...' : 'Μεταφόρτωση...')
                    : (language === 'en' ? 'Upload Logo' : 'Μεταφόρτωση Λογότυπου')
                  }
                </Button>
                
                {logoPreview && (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="w-full h-20 object-contain rounded border bg-white p-2"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={removeLogo}
                      className="absolute top-2 right-2"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>
                {language === 'en' ? 'Background Image' : 'Εικόνα Φόντου'}
              </Label>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading 
                    ? (language === 'en' ? 'Uploading...' : 'Μεταφόρτωση...')
                    : (language === 'en' ? 'Upload Background' : 'Μεταφόρτωση Φόντου')
                  }
                </Button>
                
                {backgroundPreview && (
                  <div className="relative">
                    <img 
                      src={backgroundPreview} 
                      alt="Background preview" 
                      className="w-full h-32 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={removeBackgroundImage}
                      className="absolute top-2 right-2"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div>
            <Label>
              {language === 'en' ? 'Certificate Preview' : 'Προεπισκόπηση Πιστοποιητικού'}
            </Label>
            <Card className="h-64">
              <CardContent 
                className="p-4 h-full flex flex-col items-center justify-center text-xs relative overflow-hidden"
                style={{
                  backgroundImage: backgroundPreview ? `url(${backgroundPreview})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Overlay for better text visibility */}
                {backgroundPreview && (
                  <div className="absolute inset-0 bg-white/80" />
                )}
                
                <div className="text-center space-y-2 relative z-10">
                  {/* Logo */}
                  <div className="flex justify-center mb-2">
                    {(logoPreview || formData.logo_url) ? (
                      <img src={logoPreview || formData.logo_url} alt="Logo" className="h-6 w-auto object-contain" />
                    ) : (
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 16 16" className="text-white">
                          <g stroke="currentColor" strokeWidth="1.5" fill="none">
                            <path d="M3 6 L5 8 L3 10" />
                            <path d="M13 6 L11 8 L13 10" />
                            <path d="M6 6 L10 10" />
                            <path d="M10 6 L6 10" />
                          </g>
                        </svg>
                      </div>
                    )}
                  </div>
                 
                  <div className="text-sm font-bold text-black">{formData.certificate_title || 'CERTIFICATE'}</div>
                  <div className="text-xs text-orange-500 font-semibold">{formData.certificate_subtitle || 'OF COMPLETION'}</div>
                  <div className="text-xs font-semibold text-black">Employee Name</div>
                  <div className="text-xs text-gray-600">Training Title</div>
                  
                  {/* Badge levels */}
                  <div className="flex justify-center space-x-1 mt-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
                      <span className="text-[6px] text-white font-bold">G</span>
                    </div>
                    <div className="w-3 h-3 bg-gray-400 rounded-full flex items-center justify-center">
                      <span className="text-[6px] text-white font-bold">S</span>
                    </div>
                    <div className="w-3 h-3 bg-amber-600 rounded-full flex items-center justify-center">
                      <span className="text-[6px] text-white font-bold">B</span>
                    </div>
                  </div>
                  
                  {/* Footer text */}
                  <div className="mt-4 text-[8px] text-gray-500 italic">
                    {formData.company_name || 'OVIT HR Department'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            {language === 'en' ? 'Cancel' : 'Ακύρωση'}
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Save Template' : 'Αποθήκευση Προτύπου'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};